import { Marker } from "react-map-gl"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useEffect, useMemo, useState } from "react"
import { formatNumberWithCommas } from "@nova/util-strings"
import { LngLatCoordinatePair } from "features/map/event/types"
import {
    AreaUnit,
    DistanceUnit,
    GeometryType,
    LayerIndex,
    SidePanelLayerType,
    ToolName,
} from "@nova/constants"
import { deleteRuler, updateRuler } from "features/pubsub/entityActions/cubes/ruler"
import {
    selectAllRulerBoundingBoxes,
    selectAllRulers,
    selectRulerById,
} from "features/entity-caching/rulers/selectors"
import * as Icons from "@nova/component-icons"
import { PluginRegistry } from "plugins/plugin-registry"
import { Ruler } from "@nova/protobuf"
import { zoomToRuler } from "features/map/inspector-panel/rulers/module"
import {
    getGenericLayerDataInspectorPanelComponent,
    getGenericMockableInspectorPanel,
    getGenericMockedInspectorPanelComponent,
} from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import {
    InspectorPanelRulerLayer,
    InspectorPanelRulerLayerNoTopBorder,
} from "features/map/inspector-panel/rulers/InspectorPanelRulerLayer"
import { InspectorPanelRulerData } from "features/map/inspector-panel/rulers/InspectorPanelRulerData"
import { totalDistance, calculateArea } from "@nova/calculate-geospatial"
import { convertDistance, convertArea } from "@nova/format"
import { getGenericMockedInspectorPanelHeader } from "features/map/inspector-panel/inspector-panels/headers/GenericInspectorPanelHeader"
import { getGenericMockedInspectorPanelBody } from "features/map/inspector-panel/inspector-panels/bodies/GenericInspectorPanelBody"
import { useNormalRulerLayer, useEditingRulerLayer } from "./useRulerLayer"
import { geometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { resolveEventLngLat } from "plugins/resolveLngLat"
import { clearEditingData, createNewMockRuler, selectRulerEditingData } from "./slice"
import { formatMap } from "./layers/RulerMidpointText"
import { useResizableSidePanelWidth } from "panels/content/side-panel/SidePanel"

export function registerRulerPlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "ruler",
            displayName: "Ruler",
            mockedDisplayName: "New Measurement",
            sidePanelLayerType: SidePanelLayerType.Ruler,
            PrimaryIcon: Icons.Ruler,
            updateAction: updateRuler,
            deleteAction: deleteRuler,
            confirmDelete: () => false,
            protobufMessage: Ruler,
            selectAllEntities: selectAllRulers,
            selectSingleEntity: selectRulerById,
            zoomTo: zoomToRuler,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin")
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getGenericMockableInspectorPanel({
                NonMocked: getGenericLayerDataInspectorPanelComponent({
                    spec,
                    LayerComponent: InspectorPanelRulerLayerNoTopBorder,
                    DataComponent: InspectorPanelRulerData,
                }),
                Mocked: getGenericMockedInspectorPanelComponent({
                    Header: getGenericMockedInspectorPanelHeader(spec),
                    Body: getGenericMockedInspectorPanelBody({
                        RenderComponent: InspectorPanelRulerLayer,
                    }),
                    Footer: () => {
                        const dispatch = useAppDispatch()
                        useEffect(() => {
                            dispatch(createNewMockRuler())
                            return () => {
                                geometryDataStore.free(false)
                                dispatch(clearEditingData())
                            }
                        }, [dispatch])
                        return null
                    },
                }),
            }),
            useSubtitles,
        }))
        .implement("bounding-box-data-provider", () => ({
            useBoundingBoxData: () => useAppSelector(selectAllRulerBoundingBoxes),
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: () => useNormalRulerLayer(),
            zIndex: LayerIndex.Ruler,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: () => useEditingRulerLayer(),
            zIndex: LayerIndex.GeometryBeingEdited,
        }))
        .implement("toolbox-plugin", () => ({
            name: ToolName.Ruler,
            viewerAllowed: true,
            shortcut: { letters: ["R"] },
        }))
        .implement("map-modal-plugin", () => ({
            Modal: () => {
                const editingData = useAppSelector(selectRulerEditingData)
                const points = geometryDataStore.useGeometryData("points")
                const [coords, setCoords] = useState<LngLatCoordinatePair | undefined>()
                const sidePanelWidth = useResizableSidePanelWidth()
                useEffect(() => {
                    const listener = (event: MouseEvent) => {
                        if (points.length || !editingData) return
                        if ((event.target as HTMLElement)?.className === "maplibregl-canvas") {
                            const coordinatePair = resolveEventLngLat(event)
                            setCoords([
                                coordinatePair.longitude,
                                coordinatePair.latitude,
                            ] as LngLatCoordinatePair)
                        } else {
                            setCoords(undefined)
                        }
                    }
                    window.addEventListener("mousemove", listener)
                    return () => {
                        window.removeEventListener("mousemove", listener)
                    }
                }, [editingData, points, sidePanelWidth])

                return (
                    <>
                        {editingData && !points.length && coords ? (
                            <Marker
                                longitude={coords[0]}
                                latitude={coords[1]}
                                anchor={"top-left"}
                                offset={[12, 16]}
                                style={{ pointerEvents: "none" }}
                            >
                                <div className="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                                    <small>Left click to start measurement</small>
                                </div>
                            </Marker>
                        ) : null}
                    </>
                )
            },
        }))
}

function useSubtitles(id: string) {
    const ruler = useAppSelector(selectRulerById(id))
    const [lineDistance, lineArea] = useMemo(() => {
        const distance = totalDistance(
            ruler.coordinates,
            ruler.geometryType === GeometryType.Polygon
        )
        const convertedDistance = convertDistance(
            distance,
            DistanceUnit.Meters,
            ruler.distanceUnit as DistanceUnit
        )
        const distanceString =
            formatNumberWithCommas(convertedDistance) +
            " " +
            formatMap.distance[ruler.distanceUnit as DistanceUnit]
        const geospatialCoords = ruler.coordinates
        let areaString: string | undefined = undefined
        if (ruler.coordinates.length > 2 && ruler.geometryType === GeometryType.Polygon) {
            const area = calculateArea(geospatialCoords)
            const convertedArea = convertArea(
                area,
                AreaUnit.SquareMeters,
                ruler.areaUnit as AreaUnit
            )
            areaString =
                formatNumberWithCommas(convertedArea) +
                " " +
                formatMap.area[ruler.areaUnit as AreaUnit]
        }
        return [distanceString, areaString]
    }, [ruler])
    return [lineArea, lineDistance].filter((s): s is string => !!s)
}
